<template>
  <div>
    <ModalControl v-show="show_modal" v-bind:display="show_modal" v-bind:title="lang(title)" closable id='Modal'
    :z_value="'z-50'" :default_width="default_width" @closed="closed">
    <div class="w-full">
      <Privacy v-show="show_privacy" />
      <TermsView v-show="show_terms" />
      <HelpCenterView v-show="show_contact" />
      <OurCompany v-show="show_about" />
    </div>
  </ModalControl>

  <ModalControl v-show="show_message_modal" v-bind:display="show_message_modal" :error="this.error"
    :question="this.question" :success="this.success" :note="this.warning" z_value="z-50"
    @positiveClick="handleLogout()" :positiveButton="'Yes'" @negativeClick="show_message_modal = false"
    :negativeButton="'No'" :default_width="'max-w-xl'" id='vote_modal'>
    <div class="w-full px-5 pt-2 pb-4 flex flex-col text-xl justify-center text-center items-center text-gray-600">
      {{ this.modal_message }}
    </div>
  </ModalControl>

  <ModalControl v-show="session_modal" v-bind:display="session_modal" :question="true" z_value="z-100"
    @positiveClick="stayLoggedIn()" :positiveButton="'Stay Logged In'" @negativeClick="handleLogout()"
    :negativeButton="'Log Out'" :default_width="'max-w-xl'" id='session_modal' class="z-[999]">
    <div class="flex flex-col text-center align-middle justify-center items-center w-full">
      <p class="mb-5 text-base text-center px-[5%]">No activity detected. We will log you out in <span
          class="text-yellow-500 font-bold">{{
            timerSession }} secs</span> unless you confirm you're
        still with us.</p>
    </div>
  </ModalControl>

  <Loading v-show="loading" style="z-index: 9999999999999999999999999999999999999999999;" />


  <div class="flex min-h-screen w-full">
    <!-- Sidebar: Fixed on desktop -->

    <div v-if="isDevEnvironment" class="absolute top-4 right-4 bg-error text-white rounded font-medium px-2 py-1">
      DEVELOPMENT
    </div>

    <div v-if="isStagingEnvironment" class="absolute top-4 right-4 bg-yellow-700 text-white rounded font-medium px-2 py-1">
      PRE-PRODUCTION
    </div>

    <SidebarView v-if="showSidebar" class="hidden lg:flex flex-col h-screen w-[96] fixed left-0 top-0 z-40" />

    <!-- Main content area -->
    <div class="flex flex-col flex-1 min-h-screen w-0" :class="{ 'lg:ml-96 custom-width-1600-app': showSidebar }">
      <!-- Desktop Header: Fixed -->
      <HeaderView v-if="!hide_layout" class="hidden lg:block fixed top-0 left-0 right-0 z-30" style="height:64px;"
        :title="screen_title.value" @toggleDropdown="delayedToggleDropdown" ref="dropdownToggle" />

      <!-- Desktop Dropdown (absolute, stays under header) -->
      <transition name="dropdown" enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 -translate-y-2" enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150" leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2">
        <div v-if="show_dropdown && !hide_layout" ref="dropdownMenu"
          class="bg-white rounded-lg shadow-lg flex flex-col fixed top-[64px] right-10 z-50 w-[10rem] overflow-clip font-medium">
          <span @click="validateLogout()"
            class="bg-white hover:bg-gray-50 cursor-pointer px-4 py-3 text-gray-500 flex items-center space-x-2">
            <img src="@/assets/images/papsi_logout.svg" alt="Logout Icon" class="w-4 h-4" />
            <span>Logout</span>
          </span>
        </div>
      </transition>

      <!-- Floating Mobile Ad Banner -->
      <div v-if="showMobileAd && isMobile"
        class="fixed bottom-0 left-0 right-0 z-50 flex rounded-xl items-center justify-between bg-theme-primary text-white px-4 py-3 shadow-lg m-3">
        <div class="flex items-center" @click="detectAndRedirect()">
          <img src="https://resource.pafcpic.net/assets/papsi/papsi_app.png" alt="PAPSI Logo"
            class="w-10 h-10 mr-4 rounded-md" />
          <div class="flex flex-col">
            <span class="font-semibold">Download PAPSI Mobile</span>
            <span class="font-light text-xs">Available on App Store and Google Play</span>
          </div>
        </div>
        <button @click="closeMobileAd" class="ml-4 text-white text-2xl leading-none focus:outline-none">&times;</button>
      </div>

      <!-- Mobile Top Bar (logo + menu) -->
      <div v-if="!hide_layout"
        class="h-[65px] flex items-center justify-between pb-2 bg-theme-primary text-white px-4 py-3 lg:hidden fixed top-0 left-0 right-0 z-30">
        <img src="https://resource.pafcpic.net/assets/logo/papsi_wordmark.png" alt="logo" class="h-10" />
        <button ref="mobileToggle" @click="toggleMobileMenu" class="text-white focus:outline-none">
          <svg class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      <!-- Mobile Dropdown Navigation -->
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 -translate-y-4"
        enter-to-class="opacity-100 translate-y-0" leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 -translate-y-4">
        <div v-if="show_mobile_menu && !hide_layout"
          class="lg:hidden bg-theme-primary text-white fixed left-0 right-0 z-30" ref="mobileMenu" style="top: 65px;">
          <ul>
            <li v-for="item in menuItems" :key="item.path" @click="navigate(item.path); toggleMobileMenu()"
              class="px-6 py-4 border-white cursor-pointer hover:bg-theme-sidebar-active" :class="[
                isActive(item.path)
                  ? 'bg-theme-sidebar-active'
                  : 'hover:bg-theme-sidebar-active'
              ]">
              {{ item.name }}
            </li>
            <li @click="validateLogout" class="px-6 py-4 cursor-pointer hover:bg-theme-sidebar-active">
              Logout
            </li>
            
            <li class="px-6 py-4 text-xs mt-8">
              <div class="flex flex-row items-start justify-start gap-2">
                <img
                  src="https://resource.pafcpic.net/assets/logo/papsi_iso.png"
                  class="h-12 cursor-pointer"
                  @click="openImage('https://resource.pafcpic.net/assets/logo/papsi_iso.png')"
                >

                <img
                  src="https://resource.pafcpic.net/assets/logo/papsi_dp.png"
                  class="h-12 cursor-pointer"
                  @click="openImage('https://resource.pafcpic.net/assets/logo/papsi_dp.png')"
                >
              </div>
            </li>

            <li class="px-6 py-4 opacity-50 text-xs">
              <span>{{ version }}</span>
            </li>


          </ul>
        </div>
      </transition>

      <!-- Mobile Header (welcome text, below mobile top bar) -->
      <div v-if="!hide_layout"
        class="lg:hidden bg-white shadow px-6 py-4 w-full z-10 text-gray-600 justify-center items-center text-right"
        style="margin-top:64px;">
        Welcome, <span class="font-semibold">{{ username }}!</span>
      </div>

      <!-- Main scrollable content (RouterView) -->
      <div class="flex-1 w-full overflow-y-auto" :class="{
        'lg:mt-[64px]': !hide_layout && showSidebar, // Only on desktop, only if header/sidebar needed
      }" :style="{
        marginTop: (!hide_layout && !showSidebar)
          ? (show_mobile_menu ? '112px' : '56px') // mobile: header + menu if open
          : undefined
      }">
        <transition name="slide-in" mode="out-in">
          <RouterView @loadSideBar="loadSideBar" @openContact="clicked('Contact Us')" @OpenTerms="clicked('Terms')"
            @OpenPrivacy="clicked('Privacy Policy')" @OpenAbout="clicked('About Us')" @scrollTop="scrollTop"
            class="w-full" />
        </transition>
      </div>

      <FooterView v-if="!hide_layout && !hide_footer" :copy_right_text="'PAFCPIC © ' + year" :clickables="clickables"
        @clicked="clicked" />
    </div>
  </div>
  </div>
  


</template>

<script>
import { defineComponent } from "vue";
import mixin from "@/mixin";
import ModalControl from "@/components/controls/ModalControl.vue";
import Privacy from "@/components/sections/PrivacyPolicyView.vue";
import TermsView from "@/components/sections/TermsOfUserView.vue";
import OurCompany from "@/views/static/OurCompanyView.vue"
import HelpCenterView from "@/components/sections/ContactUsView.vue";
// import Container from "@/components/sections/ContainerView.vue";
import SidebarView from "@/components/controls/SidebarView.vue";
import HeaderView from "@/components/controls/HeaderView.vue";
import FooterView from "@/components/sections/FooterView.vue";
import User from "@/services/User";
import Loading from "./components/controls/LoadingControl.vue";
import { detectAndRedirect } from "@/detectAndRedirect";


export default defineComponent({
  name: "App",
  mixins: [
    mixin,
  ],
  components: {
    // Container,
    OurCompany,
    Privacy,
    TermsView,
    ModalControl,
    HelpCenterView,
    SidebarView,
    HeaderView,
    FooterView,
    Loading
  },
  computed: {
    username() {
      return this.$store.state.credentials.username;
    },
    showSidebar() {
      return !this.hide_layout && !this.$store.state.credentials.deactivated;
    },
    isDevEnvironment() {
      return window.location.hostname.startsWith("dev");
    },
    isStagingEnvironment() {
      return window.location.hostname.startsWith("staging");
    }
  },
  provide() {
    return {
      setLoading: this.setLoading,
    };
  },
  data() {
    return {
      session_modal: false,
      idleTimer: null,
      modalTimer: null,
      idleTimeout: 300,
      modalTimeout: 30,
      timerSession: 30,
      loading: false,
      show_message_modal: false,
      modal_message: '',
      error: false,
      show_dropdown: false,
      question: false,
      success: false,
      warning: false,
      show_modal: false,
      show_privacy: false,
      show_terms: false,
      show_contact: false,
      show_about: false,
      logged: false,
      hide_layout: false,
      year: 0,
      show_bars: false,
      show_sidebar: false,
      title: '',
      default_width: '',
      showMobileAd: true,
      isMobile: false,
      clickables: ['About Us', 'Contact Us', 'Privacy Policy', 'Terms',],
      menuItems: [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'Profile', path: '/user/profile' },
        { name: 'Services', path: '/services' },
        // { name: 'Bank Account Opening', path: '/bank-account-opening' },
        // { name: 'Online BAEF', path: '/services/online-baef' },
        { name: 'Balance Inquiry', path: '/balance-inquiry' },
        
        { name: 'Events', path: '/events' },
        { name: 'Settings', path: '/user/settings' },
      ],
      show_mobile_menu: false,
      noframe: '',
      hide_footer: false,
      back_path: '',
      screen_title: {
        value: '',
      },
      route: {
        auth: '',
        passive: '',
        path: '',
        navbar: '',
      },
      excludedRoutes: [
        "login",
        "forgot-password",
        "update-password",
        "register",
        "privacy-policy",
        "about-us",
        "terms-of-use",
        "error"
      ]
    }
  },
  mounted() {
    let date = new Date();
    this.year = date.getFullYear();

    document.addEventListener('click', this.handleClickOutside);
    this.version = 'Version ' + process.env.VUE_APP_VERSION;

    this.checkIfMobile();
    window.addEventListener('resize', this.checkIfMobile);

    // 🔹 CHECK COOKIE HERE
    if (this.$cookies.get('hide_mobile_ad')) {
      this.showMobileAd = false;
    }

    if (!this.excludedRoutes.includes(this.$route.name)) {
      this.initIdleTimer();
    }

    this.$router.afterEach((to) => {
      if (!this.excludedRoutes.includes(to.name)) {
        this.initIdleTimer();
      } else {
        this.clearIdleTimer();
        this.clearModalTimer();
        this.removeActivityListeners();
      }
    });
  },

  methods: {
    openImage(url) {
      window.open(url, '_blank');
    },
    closeMobileAd() {
      this.showMobileAd = false;

      // Save cookie so it won't show again
      this.$cookies.set(
        'hide_mobile_ad',
        true,
        '7d' // expires in 7 days (you can change this)
      );
    },
    detectAndRedirect() {
      detectAndRedirect();
    },
    initIdleTimer() {
      this.clearIdleTimer();
      this.clearModalTimer();
      this.session_modal = false;
      this.addActivityListeners();
      this.resetIdleTimer();
    },
    resetIdleTimer() {
      this.clearIdleTimer();
      this.idleTimer = setTimeout(() => {
        this.showSessionModal();
      }, this.idleTimeout * 1000);
    },
    clearIdleTimer() {
      if (this.idleTimer) clearTimeout(this.idleTimer);
      this.idleTimer = null;
    },
    showSessionModal() {
      this.session_modal = true;
      this.timerSession = this.modalTimeout;
      this.startModalTimer();
    },
    startModalTimer() {
      this.clearModalTimer();

      this.modalTimer = setInterval(() => {
        if (this.timerSession > 0) {
          this.timerSession--;
        } else {
          this.clearModalTimer();
          this.session_modal = false;

          if (!this.excludedRoutes.includes(this.$route.name)) {
            let that = this;
            // that.$cookies.remove('user');
            // that.$cookies.remove('user_session');
            // that.$store.state.logged = false;
            // that.$router.push('/');

            that.handleLogout();
          }
        }
      }, 1000);
    },
    clearModalTimer() {
      if (this.modalTimer) clearInterval(this.modalTimer);
      this.modalTimer = null;
    },
    addActivityListeners() {
      this.removeActivityListeners();
      const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
      events.forEach(event => {
        window.addEventListener(event, this.onUserActivity, true);
      });
    },
    removeActivityListeners() {
      const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
      events.forEach(event => {
        window.removeEventListener(event, this.onUserActivity, true);
      });
    },
    onUserActivity() {
      if (this.excludedRoutes.includes(this.$route.name)) return;
      this.resetIdleTimer();
    },
    stayLoggedIn() {
      this.session_modal = false;
      this.clearModalTimer();
      this.resetIdleTimer();
    },
    setLoading(status) {
      this.loading = status;
    },
    loadSideBar() {
      this.show_bars = true;
    },
    isActive(path) {
      return this.$route.path === path;
    },
    dropdown() {
      this.show_dropdown = !this.show_dropdown;
    },
    delayedToggleDropdown() {
      setTimeout(() => {
        this.show_dropdown = !this.show_dropdown;
      }, 10); // Small delay to avoid conflict with outside click
    },
    handleClickOutside(event) {
      // Desktop dropdown
      const dropdown = this.$refs.dropdownMenu;
      const toggle = this.$refs.dropdownToggle?.$el;

      if (
        this.show_dropdown &&
        dropdown &&
        !dropdown.contains(event.target) &&
        toggle &&
        !toggle.contains(event.target)
      ) {
        this.show_dropdown = false;
      }

      // 🔹 Mobile dropdown FIX
      const mobileMenu = this.$refs.mobileMenu;
      const mobileToggle = this.$refs.mobileToggle;

      if (
        this.show_mobile_menu &&
        mobileMenu &&
        !mobileMenu.contains(event.target) &&
        mobileToggle &&
        !mobileToggle.contains(event.target)
      ) {
        this.show_mobile_menu = false;
      }
    },

    toggleMobileMenu() {
      this.show_mobile_menu = !this.show_mobile_menu;
    },
    navigate(path) {
      this.$router.push(path);
    },
    validateLogout() {
      this.show_dropdown = false;
      this.question = true;
      this.warning = false;
      this.error = false;
      this.success = false;
      this.modal_message = "Are you sure you want to log out?";
      this.show_message_modal = true;
    },
    handleLogout() {
      const store = this.$store;
      const cookie = this.$cookies;
      const payload = {
        hash: store.state.credentials.hash
      };
      // this.loading = true;
      const that = this;
      this.show_message_modal = false;
      this.session_modal = false;
      that.setLoading(true);
      User.logout(payload)
        .then(() => {
          const that = this;
          store.state.logged = false;
          store.state.credentials.hash = '';
          cookie.remove('user');
          cookie.remove('user_session');
          that.show_message_modal = false;
          this.clearIdleTimer();
          this.clearModalTimer();
          this.removeActivityListeners();

          this.session_modal = false;
          this.error = false;
          this.success = false;

          that.$router.push('/');
          that.setLoading(false);
        })
        .catch(() => {
          const that = this;
          store.state.logged = false;
          store.state.credentials.hash = '';
          that.session_modal = false;
          that.$emit('donelogout');
          cookie.remove('user');
          cookie.remove('user_session');
          that.$router.push('/');
          that.setLoading(false);
          that.show_message_modal = false;
        });
    },
    clicked(n) {
      if (n == 'Privacy Policy') {
        this.show_privacy = true;
        this.show_modal = true;
        this.title = ('privacy.policy.header.text');
        this.default_width = 'max-w-5xl'
      }
      if (n == 'Terms') {
        this.show_terms = true;
        this.show_modal = true;
        this.title = ('terms.header.text');
        this.default_width = 'max-w-5xl'
      }
      if (n == 'Contact Us') {
        this.show_contact = true;
        this.show_modal = true;
        this.title = ('contact.header.text');
        this.default_width = 'max-w-3xl'
      }
      if (n == 'About Us') {
        this.show_about = true;
        this.show_modal = true;
        this.title = ('About Us');
        this.default_width = 'max-w-3xl'

      }
    },
    scrollTop() {
      // console.log('scrollTop');
      window.scrollTo(0, 0);
    },
    closed() {
      this.show_privacy = false;
      this.show_contact = false;
      this.show_terms = false;
      this.show_about = false;
      this.show_modal = false;
    },
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
  },
  watch: {
    $route(to) {
      this.noframe = (to.path == "/");
      this.hide_layout = ["login", "forgot-password", "update-password", "register", "privacy-policy", "about-us", "terms-of-use", "error"].includes(to.name);
      this.back_path = '';
      this.screen_title.value = to.meta.title;
      this.route.auth = to.meta.auth;
      this.route.passive = to.meta.passive;
      this.route.path = to.path;
      this.route.navbar = to.meta.navbar;
      if (to.path == "/election/voting") {
        this.hide_footer = true;
      } else {
        this.hide_footer = false;
      }
    },

  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkIfMobile);
    document.removeEventListener('click', this.handleClickOutside);

    this.clearIdleTimer();
    this.clearModalTimer();
    this.removeActivityListeners();

  },

});
</script>

<style>
#app {
  font-family: Poppins, sans-serif;
}

.body::-webkit-scrollbar {
  display: none;
}

button:hover,
.tab:hover {
  filter: brightness(1.1) !important;
}

.otp-input {
  width: 3.2rem;
  height: 4rem;
  padding: 4px;
  font-size: 2.5rem;
  margin: 0 0.3rem;
  border-radius: 0.75rem;
  border: 0.05rem solid #dfdfdf !important;
  background-color: #f3f4f6;
  text-align: center;
  font-family: 'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif !important;
}

.otp-input::-webkit-inner-spin-button,
.otp-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.otp-input:focus {
  outline: none;
  border: 0.2rem solid #016B00 !important;
}

.text-blue-500 {
  color: #3b82f6 !important;
}

.text-yellow-500 {
  color: #eab308 !important;
}

.text-red-500 {
  color: #ef4444 !important;
}

/* Tablet (e.g., iPad) */
@media (max-width: 1024px) {
  .otp-input {
    width: 3.5rem !important;
    height: 4.5rem !important;
    font-size: 3rem !important;
    margin: 0 0.3rem;
  }
}

/* Mobile */
@media (max-width: 768px) {
  .otp-input {
    width: 3rem !important;
    height: 4rem !important;
    font-size: 2.5rem !important;
    margin: 0 0.2rem !important;
  }
}

/* Extra small mobile (e.g., older phones) */
@media (max-width: 480px) {
  .otp-input {
    width: 2.1rem !important;
    height: 2.8rem !important;
    font-size: 1.5rem !important;
    margin: 0 0.2rem !important;
    border-radius: 0.6rem !important;
  }
}


.dropdown-enter-active,
.dropdown-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}

.dropdown-enter-from,
.dropdown-leave-to {
  max-height: 0;
  opacity: 0;
}

.dropdown-enter-to,
.dropdown-leave-from {
  max-height: 500px;
  /* adjust to max expected height */
  opacity: 1;
}

/* Transition for Pages */
.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.3s ease;
}

.slide-in-enter-from {
  transform: translateY(-30px);
  opacity: 0;
}

.slide-in-enter-to {
  transform: translateY(0);
  opacity: 1;
}

.slide-in-leave-to {
  transform: translateY(-30px);
  opacity: 0;
}

/* Chrome, Edge, Safari */
input::-ms-reveal,
input::-ms-clear,
input::-webkit-contacts-auto-fill-button,
input::-webkit-credentials-auto-fill-button,
input::-webkit-clear-button,
input::-webkit-inner-spin-button {
  display: none !important;
  appearance: none;
}

/* Firefox (uses none, but good to ensure) */
input[type="password"]::-moz-placeholder {
  text-security: none;
}

@media (min-width: 1025px) and (max-width: 1600px) {
  .custom-width-1600-app {
    margin-left: 16rem !important;
    /* 320px */
  }
}
</style>
